// Runtime (DOM + timeline) introspection of the composition's editable text: whether a given
// editable node is actually visible at the current timeline pose. Natural text editing surfaces an
// edit overlay only for text visible at the rendered frame, so the editor filters regions by this.

// Effective opacity at or above this counts as "visible" for editing. Deliberately low: text that
// is only partway through a fade-in is still worth an edit affordance; only (near-)transparent text
// is skipped.
const VISIBILITY_OPACITY_THRESHOLD = 0.05;

interface GsapAnimation {
    startTime(): number;
    endTime(): number;
    getChildren?(nested: boolean, tweens: boolean, timelines: boolean): GsapAnimation[];
}

/** The subset of the GSAP timeline API this module relies on. */
export interface GsapTimelineLike extends GsapAnimation {
    duration(): number;
    time(): number;
    seek(time: number, suppressEvents?: boolean): unknown;
    getChildren(nested: boolean, tweens: boolean, timelines: boolean): GsapAnimation[];
}

/** Effective visibility of a node at the current timeline pose: opacity (incl. ancestors), display/visibility, and in-bounds. */
export function isNodeVisible(
    node: Element,
    viewportWidth: number,
    viewportHeight: number,
): boolean {
    const view = node.ownerDocument.defaultView;
    if (!view) {
        return false;
    }
    let opacity = 1;
    let element: Element | null = node;
    while (element) {
        const style = view.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }
        opacity *= parseFloat(style.opacity || '1');
        element = element.parentElement;
    }
    if (opacity < VISIBILITY_OPACITY_THRESHOLD) {
        return false;
    }
    const rect = node.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
        return false;
    }
    return (
        rect.right > 0 && rect.left < viewportWidth && rect.bottom > 0 && rect.top < viewportHeight
    );
}
